import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw, FiHome, FiMap } = FiIcons;

function MathGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [level, setLevel] = useState(1);

  const generateQuestion = () => {
    // Progressive difficulty based on age and level
    const baseLevel = Math.min(state.age, 12);
    const currentDifficulty = baseLevel + Math.floor(level / 3);
    
    let num1, num2, correctAnswer, operation;

    if (currentDifficulty <= 8) {
      // Simple addition/subtraction
      const operations = ['+', '-'];
      operation = operations[Math.floor(Math.random() * operations.length)];

      if (operation === '+') {
        num1 = Math.floor(Math.random() * 10) + 1;
        num2 = Math.floor(Math.random() * 10) + 1;
        correctAnswer = num1 + num2;
      } else {
        // Ensure strictly positive result for subtraction
        num1 = Math.floor(Math.random() * 15) + 5; // 5 to 19
        num2 = Math.floor(Math.random() * (num1 - 1)) + 1; // 1 to num1-1
        correctAnswer = num1 - num2;
      }
    } else if (currentDifficulty <= 12) {
      // Include multiplication and larger numbers
      const operations = ['+', '-', '×'];
      operation = operations[Math.floor(Math.random() * operations.length)];

      if (operation === '+') {
        num1 = Math.floor(Math.random() * 30) + 10;
        num2 = Math.floor(Math.random() * 30) + 10;
        correctAnswer = num1 + num2;
      } else if (operation === '-') {
        num1 = Math.floor(Math.random() * 50) + 25;
        // Ensure result is positive
        num2 = Math.floor(Math.random() * (num1 - 5)) + 1; 
        correctAnswer = num1 - num2;
      } else {
        num1 = Math.floor(Math.random() * 10) + 2;
        num2 = Math.floor(Math.random() * 10) + 2;
        correctAnswer = num1 * num2;
      }
    } else {
      // Advanced: Include division, fractions, and complex operations
      const operations = ['+', '-', '×', '÷'];
      operation = operations[Math.floor(Math.random() * operations.length)];

      if (operation === '+') {
        num1 = Math.floor(Math.random() * 100) + 50;
        num2 = Math.floor(Math.random() * 100) + 50;
        correctAnswer = num1 + num2;
      } else if (operation === '-') {
        num1 = Math.floor(Math.random() * 200) + 100;
        num2 = Math.floor(Math.random() * 99) + 1; // Ensure num2 < num1 significantly
        correctAnswer = num1 - num2;
      } else if (operation === '×') {
        num1 = Math.floor(Math.random() * 12) + 2;
        num2 = Math.floor(Math.random() * 12) + 2;
        correctAnswer = num1 * num2;
      } else {
        // Division - ensure clean integer division
        correctAnswer = Math.floor(Math.random() * 12) + 2;
        num2 = Math.floor(Math.random() * 10) + 2;
        num1 = correctAnswer * num2; // This ensures num1 / num2 = correctAnswer
      }
    }

    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      // Generate offsets avoiding 0 to ensure wrong != correct
      const offset = (Math.floor(Math.random() * 5) + 1) * (Math.random() > 0.5 ? 1 : -1);
      const wrong = correctAnswer + offset;
      
      // Ensure wrong answer is positive and unique
      if (wrong > 0 && wrong !== correctAnswer && !wrongAnswers.includes(wrong)) {
        wrongAnswers.push(wrong);
      }
    }

    const allAnswers = [correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);

    return {
      num1,
      num2,
      operation,
      correctAnswer,
      answers: allAnswers,
      emoji: operation === '+' ? '🍎' : operation === '-' ? '🍊' : operation === '×' ? '🌟' : '🔢'
    };
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [state.age, level]);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);
    const isCorrect = answer === currentQuestion.correctAnswer;
    const points = 10 + Math.floor(level / 2);

    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: points });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }

    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);

      if (newCompleted >= 5) {
        setGameCompleted(true);
        const bonusTokens = Math.max(5, 15 - (state.gameStats?.totalAnswers - state.gameStats?.correctAnswers || 0));
        dispatch({ type: 'GAME_COMPLETED', payload: { questId: 'math', bonusTokens: bonusTokens } });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  const nextLevel = () => {
    setLevel(prev => prev + 1);
    setQuestionsCompleted(0);
    setGameCompleted(false);
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4 pb-24"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🧮</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Math Champion!
          </h2>
          <p className="font-comic text-jungle-600 mb-4">
            Level {level} completed!
          </p>
          <p className="font-comic text-jungle-600 mb-6">
            Your math skills are growing stronger!
          </p>
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={nextLevel}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Next Level
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            <div className="grid grid-cols-2 gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-sm shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiHome} className="text-lg" />
                <span>Home</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/quest-map')}
                className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-3 rounded-2xl font-kid text-sm shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMap} className="text-lg" />
                <span>Quest Map</span>
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const shouldShowVisual = currentQuestion.operation === '+' || currentQuestion.operation === '-';
  const showCount = shouldShowVisual && currentQuestion.num1 <= 10 && currentQuestion.num2 <= 10;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4 pb-24"
    >
      <div className="max-w-4xl mx-auto">
        {/* Enhanced Header with Navigation - Added padding left to avoid Avatar overlap */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8 pl-24 transition-all duration-300"
        >
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
            >
              <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
              <span className="font-comic text-jungle-700">Back</span>
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
            >
              <SafeIcon icon={FiHome} className="text-jungle-600" />
              <span className="font-comic text-jungle-700">Home</span>
            </button>
          </div>
          <div className="text-center">
            <h1 className="text-3xl font-kid text-jungle-700">Math Jungle 🧮</h1>
            <p className="font-comic text-jungle-600">Level {level}</p>
          </div>
          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-green-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-8">
            Solve this math problem!
          </h2>

          {/* Math Problem Display */}
          <div className="flex justify-center items-center mb-8 space-x-4 flex-wrap">
            {/* First number with optional visual */}
            <div className="text-center">
              {showCount && (
                <div className="grid grid-cols-5 gap-1 mb-2 max-w-40">
                  {Array.from({ length: Math.min(currentQuestion.num1, 20) }, (_, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="text-lg"
                    >
                      {currentQuestion.emoji}
                    </motion.div>
                  ))}
                </div>
              )}
              <div className="text-4xl font-kid text-jungle-700">{currentQuestion.num1}</div>
            </div>

            {/* Operation */}
            <div className="text-6xl font-kid text-safari-600">{currentQuestion.operation}</div>

            {/* Second number with optional visual */}
            <div className="text-center">
              {showCount && (
                <div className="grid grid-cols-5 gap-1 mb-2 max-w-40">
                  {Array.from({ length: Math.min(currentQuestion.num2, 20) }, (_, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: (currentQuestion.num1 + index) * 0.05 }}
                      className="text-lg"
                    >
                      {currentQuestion.emoji}
                    </motion.div>
                  ))}
                </div>
              )}
              <div className="text-4xl font-kid text-jungle-700">{currentQuestion.num2}</div>
            </div>

            {/* Equals */}
            <div className="text-6xl font-kid text-safari-600">=</div>

            {/* Question mark */}
            <div className="text-6xl font-kid text-purple-600">?</div>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentQuestion.answers.map((answer) => (
              <motion.button
                key={answer}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => !showResult && handleAnswerSelect(answer)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-kid text-3xl shadow-lg transition-all duration-200 ${
                  showResult
                    ? answer === currentQuestion.correctAnswer
                      ? 'bg-green-400 text-white'
                      : answer === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                {answer}
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <div className="text-green-600 font-kid text-xl">
                    🎉 Fantastic! {currentQuestion.num1} {currentQuestion.operation} {currentQuestion.num2} = {currentQuestion.correctAnswer}
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl">
                    Keep trying! {currentQuestion.num1} {currentQuestion.operation} {currentQuestion.num2} = {currentQuestion.correctAnswer}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default MathGame;